import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useI18n } from "@faceheart/i18n";
import { dashboardApi } from "../lib/api/endpoints";
import { formatAlertMessage, formatAlertType } from "../lib/alerts/format";

export function DashboardPage() {
  const { t } = useI18n("admin");
  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard", "overview", "7d"],
    queryFn: () => dashboardApi.overview("7d"),
  });

  if (isLoading) {
    return <div className="p-8 text-admin-text2">{t("dashboard.loading")}</div>;
  }
  if (error) {
    return (
      <div className="p-8 text-admin-red">
        {t("dashboard.loadFailed", { message: (error as Error).message })}
      </div>
    );
  }
  if (!data) return null;

  const k = data.kpis;
  const cards = [
    { label: t("dashboard.kpis.employeesTotal"), value: k.employees_total, color: "text-admin-text1" },
    { label: t("dashboard.kpis.measuredToday"), value: k.employees_measured_today, color: "text-admin-cyan" },
    { label: t("dashboard.kpis.healthy"), value: k.healthy, color: "text-admin-green" },
    { label: t("dashboard.kpis.alertsOpen"), value: k.alerts_open, color: "text-admin-red" },
    // No device KPIs: the device-auth axis was removed (ADR-016) — the backend
    // no longer returns devices_active/total and the devices table is dropped.
  ];

  return (
    <div className="p-8">
      <h1 className="text-xl font-medium mb-6">{t("dashboard.title")}</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <div
            key={c.label}
            className="bg-admin-bg2 border border-admin-border rounded-lg p-4"
          >
            <div className="text-admin-text3 text-xs mb-2">{c.label}</div>
            <div className={`text-2xl font-mono-display ${c.color}`}>
              {c.value}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <section className="lg:col-span-2 bg-admin-bg2 border border-admin-border rounded-lg p-4">
          <h2 className="text-sm text-admin-text2 mb-4">{t("dashboard.latestAlertsCard")}</h2>
          {data.latest_alerts.length === 0 ? (
            <div className="text-admin-text3 text-sm py-8 text-center">
              {t("dashboard.noAlerts")}
            </div>
          ) : (
            <ul className="space-y-2">
              {data.latest_alerts.slice(0, 7).map((a) => (
                <li
                  key={a.id}
                  className="flex items-center justify-between border-b border-admin-border last:border-0 pb-2 last:pb-0"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className={`inline-block w-1.5 h-1.5 rounded-full ${
                        a.severity === "critical"
                          ? "bg-admin-red"
                          : a.severity === "warning"
                            ? "bg-admin-orange"
                            : "bg-admin-cyan"
                      }`}
                    />
                    <Link
                      to={`/employees/${a.employee_id}`}
                      className="text-sm font-medium text-admin-cyan hover:underline shrink-0"
                    >
                      {a.employee_name || a.employee_code || t("alerts.row.unknownEmployee")}
                    </Link>
                    <span className="text-sm text-admin-text2 truncate">
                      {formatAlertMessage(t, a)}
                    </span>
                  </div>
                  <span className="text-xs text-admin-text3 shrink-0 ml-2">
                    {formatAlertType(t, a.type)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="bg-admin-bg2 border border-admin-border rounded-lg p-4">
          <h2 className="text-sm text-admin-text2 mb-4">{t("dashboard.todayMeasureCard")}</h2>
          <div className="text-admin-text3 text-xs">{t("dashboard.totalScans")}</div>
          <div className="text-3xl font-mono-display text-admin-cyan mt-1">
            {data.today_stats.total_scans}
          </div>
          <div className="text-admin-text3 text-xs mt-4">{t("dashboard.uniqueUsers")}</div>
          <div className="text-3xl font-mono-display text-admin-text1 mt-1">
            {data.today_stats.unique_users}
          </div>
        </section>
      </div>
    </div>
  );
}
